import Promise from 'bluebird';
import * as Log from '../util/log';
import { TEST } from '../constants';
import { isNil, isNotNil } from '../util/object';
import { captureExceptionOnce } from '../util/sentry';

const UNSUPPORTED_ENV_MSG =
  'Posting messages to workers is currently not supported in our TEST environment';

/**
 * @class FunctionalWorkerService.
 * Provides an abstraction layer to interact with `workers` in a functional way.
 */
export default class FunctionalWorkerService {
  /* @type {Worker} */
  _workerInstance = null;

  _messageId = 0;
  _messagePromiseResolvers = new Map();
  _messagePromiseRejecters = new Map();

  /**
   * @constructor
   * @param  {worker-loader-ref} WorkerLoader "Generator" for instantiating a new worker instance.
   * See https://github.com/webpack-contrib/worker-loader
   */
  constructor(WorkerLoader) {
    if (isNil(WorkerLoader)) {
      throw new Error('Please provide an adequate Worker Loader Reference');
    }
    this._workerInstance = new WorkerLoader();

    // Response to worker message
    // Looks up promise handlers by messageId and uses them.
    this._workerInstance.onmessage = (event) => {
      const { data } = event;
      const { error, messageData, messageId } = data;
      const resolver = this._messagePromiseResolvers.get(messageId);
      const rejecter = this._messagePromiseRejecters.get(messageId);

      if (isNil(resolver) || isNil(rejecter)) return;

      if (isNotNil(error)) {
        rejecter(error);
      } else {
        resolver(messageData);
      }

      // Delete unneeded handlers
      this._messagePromiseResolvers.delete(messageId);
      this._messagePromiseRejecters.delete(messageId);
    };

    /**
     * @function onerror
     * @param  {ErrorEvent} event
     * https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Using_web_workers#Handling_errors
     */
    this._workerInstance.onerror = (event) => {
      const error = `The following error: ${event.message}\n occurred in worker file: ${event.filename}
                       lineno: ${event.lineno} colno: ${event.colno}`;
      Log.error(error, event);
      captureExceptionOnce(error, { event });

      // TODO(lemieux) figure out how to target the right rejecter
      const errorEvent = new Error(error);
      this._messagePromiseRejecters.forEach((rejecter, messageId) => {
        rejecter(errorEvent);
        this._messagePromiseResolvers.delete(messageId);
        this._messagePromiseRejecters.delete(messageId);
      });
    };
  }

  /**
   * @function onMessage
   * @param   {String} messageType String that represents what type of return object is recieved from worker
   * @param   {Object} data     Returned object from worker
   * @returns {Promise} Promise which resolves with the data returned from the worker
   */
  postMessage = (messageType, data) => {
    if (TEST) return Promise.reject(new Error(UNSUPPORTED_ENV_MSG));

    // Increment the message id
    this._messageId++;

    // Get promise handlers
    const { promise, resolver, rejecter } = this.createMessagePromise(
      this._messageId,
    );
    this._messagePromiseRejecters.set(this._messageId, rejecter);
    this._messagePromiseResolvers.set(this._messageId, resolver);

    // Send message to worker
    const message = {
      data,
      messageId: this._messageId,
      type: messageType,
    };

    this._workerInstance.postMessage(message);

    return promise;
  };

  createMessagePromise(messageId) {
    let resolver;
    let rejecter;
    const promise = new Promise((resolve, reject) => {
      rejecter = reject;
      resolver = resolve;
    });

    promise.catch((error) => {
      Log.warn(error);
    });

    return {
      promise,
      rejecter,
      resolver,
    };
  }
}
